<template>
  <fullscreen ref="fullscreen" @change="fullscreenChange" class="fullscreen">
    <div class="canvas-container" ref="canvasContainer">
      <div class="canvas" ref="bpmnCanvas"></div>

      <div class="misc-controls">
        <ul class="control-list horizontal-list" v-if="extraButtons.length">
          <li v-for="(extraButton, i) in extraButtons" :key="i">
            <span class="group pa-1">
              <v-btn
                flat
                icon
                @click="extraButton.method(), (loader = 'loading')"
                :title="extraButton.title"
                :loading="loading"
              >
                <v-icon>{{ extraButton.icon }}</v-icon>
              </v-btn>
            </span>
          </li>
        </ul>
      </div>

      <div class="display-controls">
        <ul
          class="mt-3 control-list"
          v-if="propertiesToggleState || paletteToggleState || minimapToggle"
        >
          <li v-if="propertiesToggleState">
            <span
              ><v-icon
                title="Toggle Properties"
                v-on:click="toggleProperties"
                color="#424242"
                >{{ propertiesStateIcon }}</v-icon
              ></span
            >
          </li>
          <li v-if="propertiesToggleState && paletteToggleState">
            <hr />
          </li>
          <li v-if="paletteToggleState">
            <v-icon
              title="Toggle Palette"
              v-on:click="togglePalette"
              color="#424242"
              >{{ paletteStateIcon }}
            </v-icon>
          </li>
          <li v-if="paletteToggleState && minimapToggle">
            <hr />
          </li>
          <li v-if="minimapToggle">
            <v-icon
              title="Toggle MiniMap"
              v-on:click="toggleMinimap"
              color="#424242"
              >{{ minimapStateIcon }}
            </v-icon>
          </li>
        </ul>
        <ul class="mt-3 control-list" v-if="fullScreenToggle">
          <li>
            <v-icon
              title="Toggle Fullscreen"
              v-on:click="toggleFullscreen"
              color="#424242"
              >{{ fullscreenStateIcon }}
            </v-icon>
          </li>
        </ul>

        <ul class="mt-3 control-list" v-if="zoomResetButton">
          <li>
            <v-icon
              title="reset zoom"
              v-on:click="zoomReset"
              color="#424242"
              medium
              >center_focus_strong
            </v-icon>
          </li>
        </ul>

        <ul class="mt-3 control-list" v-if="zoomInButton || zoomOutButton">
          <li v-if="zoomInButton">
            <v-icon title="zoom in" v-on:click="zoomIn" color="#424242" medium
              >zoom_in
            </v-icon>
          </li>
          <li v-if="zoomOutButton && zoomInButton">
            <hr />
          </li>
          <li v-if="zoomOutButton">
            <v-icon title="zoom out" v-on:click="zoomOut" color="#424242" medium
              >zoom_out
            </v-icon>
          </li>
        </ul>
      </div>
    </div>
    <div
      v-if="propertiesPanel"
      :class="{ 'properties-panel-hide': propertiesPanelHide }"
      class="properties-panel-parent"
      ref="propertiesPanel"
    ></div>
  </fullscreen>
</template>

<script>
import BpmnNavViewer from 'bpmn-js/lib/NavigatedViewer' // Todo: Import from dist to reduce size?
import BpmnModeler from 'bpmn-js/lib/Modeler'
import propertiesPanelModule from 'bpmn-js-properties-panel'

import minimapModule from 'diagram-js-minimap'
import tokenSimulation from 'bpmn-js-token-simulation'
import _ from 'lodash'
import { mapState } from 'vuex'

export default {
  props: {
    toMark: {
      type: Array,
      default() {
        return []
      }
    },
    toHighlight: {
      type: Array,
      default() {
        return []
      }
    },
    bpmnXml: {
      type: String,
      default: null
    },
    fullScreenToggle: {
      type: Boolean,
      default: false
    },
    zoomResetButton: {
      type: Boolean,
      default: false
    },
    zoomInButton: {
      type: Boolean,
      default: false
    },
    zoomOutButton: {
      type: Boolean,
      default: false
    },
    propertiesPanel: {
      type: Boolean,
      default: false
    },
    propertiesToggle: {
      type: Boolean,
      default: true
    },
    paletteToggle: {
      type: Boolean,
      default: true
    },
    minimapToggle: {
      type: Boolean,
      default: false
    },
    tokenSimulationToggle: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: false
    },
    extraButtons: {
      type: Array,
      default() {
        return []
      }
    },
    providerModules: {
      type: Array,
      default() {
        return []
      }
    },
    moddleDescriptors: {
      type: Object,
      default() {
        return {}
      }
    },
    nodesToBeMarked: {
      type: Array,
      default() {
        return []
      }
    },
    tagWorkflowItems: {
      type: Boolean,
      default() {
        return false
      }
    }
  },

  data: function() {
    return {
      bpmnCanvas: null,
      canvasContainer: null,
      loading: false,
      loader: null,
      fullscreenStateIcon: 'fullscreen',
      propertiesStateIcon: 'chevron_right',
      paletteStateIcon: 'palette',
      minimapStateIcon: 'layers_clear',
      propertiesToggleState: false,
      paletteToggleState: false,
      propertiesPanelHide: false,
      events: [
        // 'element.hover',
        // 'element.out',
        'element.click',
        'element.dblclick'
        // 'element.mousedown',
        // 'element.mouseup'
      ]
    }
  },

  watch: {
    bpmnXml() {
      this.generateDiagram()
    },
    toMark() {
      this.handleMarking()
    },
    toHighlight() {
      this.handleHighlight()
    },
    nodesToBeMarked() {
      this.generateDiagram()
    },
    tagWorkflowItems() {
      this.generateDiagram()
    },
    loader() {
      const l = this.loader
      this[l] = !this[l]
      setTimeout(() => (this[l] = false), 3000)
      this.loader = null
    }
  },

  computed: {
    ...mapState({
      hives: state => state.bots.botHive
    }),
    propertiesToggleStateCompute() {
      return this.propertiesPanel && this.propertiesToggle
    },
    paletteToggleStateCompute() {
      return this.paletteToggle && this.editable
    }
  },

  methods: {
    generateDiagram: function() {
      this.bpmnCanvas.importXML(this.bpmnXml, err => {
        if (err) {
          console.log('error rendering', err)
        } else {
          // console.log('rendered')
          this.bpmnCanvas.get('zoomScroll').reset()
          if (!this.editable && this.nodesToBeMarked.length > 0) {
            let overlays = this.bpmnCanvas.get('overlays')
            try {
              // add metrics overlay to node, in view mode
              this.nodesToBeMarked.forEach(node => {
                console.log('marking: ', node.id)
                try {
                  overlays.add(node.id, {
                    position: node.position,
                    html: node.html
                  })
                } catch (e) {
                  // nothing
                  console.log(e)
                }
              })
            } catch (e) {
              // nothing
              console.log(e)
            }
          }
          let nodeTags = this.parseBPMN(this.bpmnXml)
          if (this.tagWorkflowItems && nodeTags.length > 0) {
            let overlays = this.bpmnCanvas.get('overlays')
            try {
              // add metrics overlay to node, in view mode
              nodeTags.forEach(node => {
                console.log('tagging: ', node.id)
                try {
                  overlays.add(node.id, {
                    position: node.position,
                    html: node.html
                  })
                } catch (e) {
                  // nothing
                  console.log(e)
                }
              })
            } catch (e) {
              // nothing
              console.log(e)
            }
          }
        }
      })
      this.handleEvents()
    },

    format(input) {
      return _.replace(input, /\./g, '_')
    },

    async handleEvents() {
      const eventBus = this.bpmnCanvas.get('eventBus')
      const self = this
      this.events.forEach(function(event) {
        eventBus.on(event, function(e) {
          let eventName = self.format(event)
          self.$emit(eventName, e)
        })
      })
    },

    handleMarking() {
      this.toMark.forEach(item => {
        this.addMarker(item.elementId, item.className, item.symbol)
      })
    },

    handleHighlight() {
      this.toMark.forEach(item => {
        this.highlight(item.elementId, item.marker)
      })
    },

    addMarker(elementId, className, symbol) {
      let overlays = this.bpmnCanvas.get('overlays')

      try {
        // attach an overlay to a node
        overlays.add(elementId, 'diff', {
          position: {
            top: -12,
            right: 12
          },
          html: '<span class="marker ' + className + '">' + symbol + '</span>'
        })
      } catch (e) {
        // nothing
      }
    },

    highlight(elementId, marker) {
      this.bpmnCanvas.get('canvas').addMarker(elementId, marker)
    },

    unhighlight(elementId, marker) {
      this.bpmnCanvas.get('canvas').removeMarker(elementId, marker)
    },

    zoomReset: function() {
      if (this.zoomResetButton) {
        this.bpmnCanvas.get('zoomScroll').reset()
      }
    },

    zoomIn: function() {
      if (this.zoomInButton) {
        this.bpmnCanvas.get('zoomScroll').stepZoom(1)
      }
    },

    zoomOut: function() {
      if (this.zoomOutButton) {
        this.bpmnCanvas.get('zoomScroll').stepZoom(-1)
      }
    },

    toggleFullscreen: function() {
      if (this.fullScreenToggle) {
        this.$refs.fullscreen.toggle()
      }
    },

    toggleProperties: function() {
      if (this.propertiesToggleState) {
        this.propertiesPanelHide = !this.propertiesPanelHide
        this.propertiesChange(this.propertiesPanelHide)
      }
    },

    togglePalette: function() {
      if (this.paletteToggle) {
        this.bpmnCanvas.get('palette').toggle()
      }
    },

    toggleMinimap: function() {
      if (this.minimapToggle) {
        this.bpmnCanvas.get('minimap').toggle()
        this.minimapChange(this.bpmnCanvas.get('minimap').isOpen())
      }
    },

    fullscreenChange: function(isFullscreen) {
      if (isFullscreen) {
        this.fullscreenStateIcon = 'fullscreen_exit'
      } else {
        this.fullscreenStateIcon = 'fullscreen'
      }
    },

    propertiesChange: function(isOpen) {
      if (isOpen) {
        this.propertiesStateIcon = 'menu'
      } else {
        this.propertiesStateIcon = 'chevron_right'
      }
    },

    minimapChange: function(isOpen) {
      if (isOpen) {
        this.minimapStateIcon = 'layers'
      } else {
        this.minimapStateIcon = 'layers_clear'
      }
    },

    getBpmnCanvas: function() {
      return this.bpmnCanvas
    },

    parseBPMN(xml) {
      let elementHTML =
        '<div style="background: gray; color: white; padding: 5px; display: flex;align-items: center;justify-content: center;height: 30px; max-width: 200px; white-space: nowrap; border-radius: 10px">'
      let nodesToBeTagged = []
      //parse bpmn xml
      let parser = new DOMParser()
      let xmlDoc = parser.parseFromString(xml, 'text/xml')
      //get all the elements with tag name bpmn:serviceTask
      let serviceTasks = xmlDoc.getElementsByTagName('bpmn:serviceTask')
      //get all the elements with tag name bpmn:userTask
      let userTasks = xmlDoc.getElementsByTagName('bpmn:userTask')
      //iterate over all the service tasks
      Array.from(serviceTasks).forEach(element => {
        let node = {}
        node.id = element.getAttribute('id')
        node.position = {
          top: -15,
          right: 40
        }
        let name = ''
        let customClass = element.getAttribute('camunda:class')
        if (!customClass) return

        switch (customClass) {
          case 'com.beezlabs.camundaengine.async.Appender':
            name = 'Appender'
            break
          case 'com.beezlabs.camundaengine.async.Bot':
            let customAgentType = element.getAttribute('tulip:hiveType')
            let customAgent = element.getAttribute('tulip:hive')
            //find the hive matching the identified Agent
            let hive = null
            if (this.hives && this.hives.length > 0) {
              hive = this.hives.find(hive => {
                return hive.hexId === customAgent
              })
            }
            if (customAgentType) {
              if (customAgentType === 'JAVA') {
                name = 'Java Bot'
              } else if (customAgentType === 'RPA') {
                name = 'RPA Bot'
              } else if (customAgentType === 'SAP') {
                name = 'SAP Bot'
              }
            } else name = 'Bot'
            name = name + (hive ? ' (' + hive.name + ')' : '')
            break
          case 'com.beezlabs.camundaengine.async.BotTrigger':
            name = 'Bot Trigger'
            break
          case 'com.beezlabs.camundaengine.async.Iterator':
            name = 'Iterator'
            break
          case 'com.beezlabs.camundaengine.async.Refactor':
            name = 'Refactor'
            break
          case 'com.beezlabs.camundaengine.async.UnMap':
            name = 'UnMap'
            break
          case 'com.beezlabs.camundaengine.async.WorkflowTrigger':
            name = 'Workflow trigger'
            break
          default:
            name = 'undefined'
        }

        node.html = elementHTML + name + '</div>'
        nodesToBeTagged.push(node)
      })
      Array.from(userTasks).forEach(element => {
        let node = {}
        node.id = element.getAttribute('id')
        node.position = {
          top: -15,
          right: 40
        }
        node.html = elementHTML + 'User Task</div>'
        nodesToBeTagged.push(node)
      })
      return nodesToBeTagged
    },
    init() {
      let options = { container: this.$refs.bpmnCanvas }
      let additionalModules = []
      if (this.propertiesPanel && this.editable) {
        options.propertiesPanel = { parent: this.$refs.propertiesPanel }
        additionalModules.push(propertiesPanelModule)
        for (let propertiesProvider of this.providerModules) {
          additionalModules.push(propertiesProvider)
        }
      }
      if (this.minimapToggle) {
        additionalModules.push(minimapModule)
      }
      if (this.tokenSimulationToggle) {
        additionalModules.push(tokenSimulation)
      }
      if (additionalModules.length > 0) {
        options.additionalModules = additionalModules
      }
      if (this.editable) {
        options.moddleExtensions = this.moddleDescriptors
        this.bpmnCanvas = new BpmnModeler(options)
      } else {
        this.bpmnCanvas = new BpmnNavViewer(options)
      }
      this.generateDiagram()
    }
  },

  created: function() {
    this.propertiesToggleState = this.propertiesToggleStateCompute
    this.paletteToggleState = this.paletteToggleStateCompute
  },

  mounted: function() {
    this.init()
  }
}
</script>

<style scoped>
.fullscreen {
  height: 100%;
  display: flex;
}

.canvas {
  height: 100%;
  width: 100%;
  position: absolute;
}

.canvas-container {
  background: #fafafa;
  height: 100%;
  position: relative;
  flex: 1 1 auto;
}

.misc-controls {
  z-index: 11;
  position: absolute;
  top: 5px;
  left: 50%;
  margin-right: -50%;
  transform: translate(-50%, 0);
}

.display-controls {
  position: absolute;
  top: 0;
  right: 0;
  width: 35px;
}

.control-list {
  background: #f8f8f8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  border-radius: 2px;
  padding: 5px;
  list-style: none;
  margin: 0;
}

.control-list button {
  padding: 0;
  outline: none;
  cursor: pointer;
  font-size: 22px;
  line-height: 26px;
  color: #555555;
  background: none;
  border: none;
}

.control-list button:hover {
  color: #333333;
}

.control-list.horizontal-list,
.control-list.horizontal-list li {
  display: inline-block;
}

.control-list.horizontal-list button {
  margin: 0 5px;
}

.properties-panel-parent {
  flex: 0 1 auto;
  background: #f8f8f8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  border-radius: 2px;
  padding: 5px;
  position: relative;
  top: 0;
  bottom: 0;
  right: 0;
  width: 260px;
  z-index: 1;
  border-left: 1px solid #ccc;
  overflow: auto;
}

.properties-panel-hide {
  display: none;
}

.properties-panel-parent:empty {
  display: none;
}
</style>

<style>
.djs-minimap:not(.open) {
  display: none !important;
}

.djs-minimap .toggle {
  display: none !important;
}

.bjs-powered-by {
  left: 0 !important;
  bottom: 0 !important;
  right: unset !important;
}

.djs-palette {
  left: 0 !important;
  top: 0 !important;
}

.djs-palette:not(.open) {
  display: none !important;
}

.djs-palette-toggle {
  display: none;
}

.content {
  height: 100%;
  width: 100%;

  position: relative;
  display: table;
}

.di-container {
  height: 100%;
  width: 50%;
  position: relative;

  display: table-cell;
  border-collapse: collapse;
}

.di-container.left {
  border-right: dotted 2px #ccc;
}

.di-header {
  position: absolute;
  top: 0;
  z-index: 10;
  margin: 20px;
  padding: 10px;

  background: #eee;
  border-radius: 5px;

  opacity: 0.9;
}

.di-header:hover {
  opacity: 1;
}

.di-header h2 {
  margin-top: 0;
}

.right .di-header {
  right: 0;
  text-align: right;
}

.canvas {
  display: block;
  height: 100%;
}

.djs-shape.highlight .djs-outline {
  stroke: yellow !important;
  stroke-width: 10px;
  fill: yellow;
}

.djs-container {
  overflow: hidden;
}

/**
 * drag and drop
 */

.drop-marker {
  border: dashed 5px #ccc;
  border-radius: 20px;

  position: absolute;
  left: 20px;
  top: 20px;
  right: 20px;
  bottom: 20px;
}

.di-container .drop-marker,
.di-container.dropping :not(.drop-marker) {
  display: none;
}

.dropping .drop-marker {
  display: block;
}

/**
 * change details
 */
.changeDetails table {
  border: 2px solid orange;
  width: 300px;
  background-color: #fff;
}

.changeDetails th {
  text-align: left;
}

/**
 * markers
 */
.marker {
  border-radius: 50%;
  background-color: gray;
  color: white;
  font-weight: bold;
  font-size: 150%;
  padding-right: 6px;
  padding-left: 6px;
}

.marker-changed {
  font-size: 130%;
  padding: 2px 5px;
}

.marker-layout-changed {
  font-size: 120%;
  padding: 2px 4px;
}

/**
 * changes overview
 */
#changes-overview {
  position: absolute;
  left: 25%;
  right: 25%;
  bottom: 0;
  height: 40%;

  color: #666;

  padding-top: 40px;
  z-index: 110;
}

#changes-overview.collapsed {
  height: 45px;
}

#changes-overview .changes {
  padding: 20px;
  border-radius: 2px 0;

  position: relative;
  height: 100%;
  overflow-y: auto;
}

#changes-overview table {
  text-align: left;
  width: 100%;
  height: 100%;

  border-collapse: collapse;
}

#changes-overview table tr {
  line-height: 30px;
}

#changes-overview thead tr {
  border-bottom: solid 1px #999;
}

#changes-overview .entry:hover {
  background-color: #eee;
  cursor: pointer;
}

#changes-overview .status {
  font-weight: bold;
  color: #f9f9f9;

  padding: 0 5px;
  display: inline-block;
  white-space: nowrap;

  line-height: 20px;
}

#changes-overview .entry.removed .status {
  background-color: red;
}

#changes-overview .entry.added .status {
  background-color: green;
}

#changes-overview .entry.changed .status {
  background-color: orange;
}

#changes-overview .entry.layout-changed .status {
  background-color: blue;
}

#changes-overview .show-hide-toggle {
  margin: 0 0 0 auto;
  padding: 5px 10px;
  height: 40px;
  cursor: pointer;
  background: #f0f0f0;
  display: inline-block;
  line-height: 30px;

  position: absolute;
  right: 20px;
  top: 0;

  border-radius: 5px 5px 0 0;
  cursor: pointer;
}

#changes-overview .show-hide-toggle:hover {
  background: #ccc;
}
* {
  box-sizing: border-box;
}

html,
body {
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;

  font-size: 12px;
  color: #444;

  height: 100%;
  padding: 0;
  margin: 0;
}

h1,
h2,
h3,
h4 {
  font-weight: normal;
}

.content {
  width: 100%;
  height: 100%;

  overflow: hidden;
}

/** widgets **/

.bjs-powered-by,
.io-control {
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  border-radius: 2px;
  padding: 5px;
}

.bjs-powered-by {
  padding-bottom: 2px;
}

.io-control-list {
  list-style: none;

  padding: 5px;
  margin: 0;
}

button.inactive,
a.inactive {
  color: #e0e0e0;
  color: rgba(10, 10, 10, 0.4) !important;
  cursor: default;
}

.close {
  font-size: 21px;
  font-weight: 700;
  text-shadow: 0 1px 0 #fff;
  opacity: 0.2;
  line-height: 1;
  vertical-align: middle;
  margin-left: 5px;
}

a:link {
  text-decoration: none;
}

/*//button,*/
/*//a,*/
/*//a:visited {*/
/*//  color: #555;*/
/*//}*/
/*//*/
/*//button:hover,*/
/*//a:hover {*/
/*//  color: #333;*/
/*//}*/

.vr {
  display: inline-block;
  height: 26px;
  border-right: solid 1px #999;
  vertical-align: top;
}

.io-control-list a,
.io-control-list button {
  padding: 0;
  outline: none;

  cursor: pointer;
  font-size: 22px;
  line-height: 26px;

  background: none;
  border: none;
}

.io-control-list.io-horizontal,
.io-control-list.io-horizontal li {
  display: inline-block;
}

.io-control-list.io-horizontal a,
.io-control-list.io-horizontal button {
  padding: 2px;
  margin: 0 5px;
}

.io-control hr {
  border: none;
  border-top: solid 1px #eee;
}

/** dialogs */

.io-dialog {
  display: none;
}

.io-dialog.open {
  display: block;
}

.io-dialog.open:before {
  content: '';
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;

  background: #666;
  opacity: 0.2;

  z-index: 1001;
}

.io-dialog .content {
  position: fixed;

  background: white;
  padding: 10px 30px 20px 30px;

  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  border-radius: 2px;

  height: auto;

  z-index: 1001;
}

.io-dialog .project-logo {
  position: absolute;
  bottom: 27px;
  right: 27px;
  width: 54px;
  height: 54px;
  font-size: 48px;
}

@media (max-width: 599px) {
  .io-dialog .content {
    left: 20px;
    right: 20px;
    top: 20px;
    width: auto;
  }
}

@media (min-width: 600px) {
  .io-dialog .content {
    width: 600px;
    left: 50%;
    margin-left: -300px;
    top: 100px;
  }
}

/** fonts **/

[class^='icon-']:before,
[class*=' icon-']:before {
  font-family: 'bpmnio';
  font-style: normal;
  font-weight: normal;
  speak: none;

  display: inline-block;
  text-decoration: inherit;
  width: 1em;
  text-align: center;

  font-variant: normal;
  text-transform: none;

  line-height: 1em;
}

.icon-plus:before {
  content: '\e800';
}
.icon-loading:before {
  content: '\e801';
}
.icon-minus:before {
  content: '\e802';
}
.icon-open:before {
  content: '\e803';
}
.icon-picture:before {
  content: '\e804';
}
.icon-download:before {
  content: '\e805';
}
.icon-size-reset:before {
  content: '\e806';
}
.icon-bpmn-io:before {
  content: '\e807';
}
.icon-info:before {
  content: '\e808';
}
.icon-comment:before {
  content: '\e809';
}
.icon-undo:before {
  content: '\e80a';
}
.icon-redo:before {
  content: '\e80b';
}
.icon-plus-circled:before {
  content: '\e80c';
}

/** animate spinner **/

.animate-spin {
  -moz-animation: spin 2s infinite linear;
  -o-animation: spin 2s infinite linear;
  -webkit-animation: spin 2s infinite linear;
  animation: spin 2s infinite linear;
  display: inline-block;
}
@-moz-keyframes spin {
  0% {
    -moz-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }

  100% {
    -moz-transform: rotate(359deg);
    -o-transform: rotate(359deg);
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
  }
}
@-webkit-keyframes spin {
  0% {
    -moz-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }

  100% {
    -moz-transform: rotate(359deg);
    -o-transform: rotate(359deg);
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
  }
}
@-o-keyframes spin {
  0% {
    -moz-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }

  100% {
    -moz-transform: rotate(359deg);
    -o-transform: rotate(359deg);
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
  }
}
@-ms-keyframes spin {
  0% {
    -moz-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }

  100% {
    -moz-transform: rotate(359deg);
    -o-transform: rotate(359deg);
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
  }
}
@keyframes spin {
  0% {
    -moz-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }

  100% {
    -moz-transform: rotate(359deg);
    -o-transform: rotate(359deg);
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
  }
}

/**/

.diff-removed:not(.djs-connection) .djs-visual > :nth-child(1) {
  fill: #e56283 /* light-red */ !important;
}

.diff-removed.djs-connection .djs-visual > :nth-child(1) {
  stroke: #bb163f /* red */ !important;
}

.marker.marker-removed {
  background: #bb163f /* red */;
}

.diff-added:not(.djs-connection) .djs-visual > :nth-child(1) {
  fill: #90dd5f /* light-green */ !important;
}

.diff-added.djs-connection .djs-visual > :nth-child(1) {
  stroke: #54b415 /* green */ !important;
}

.marker.marker-added {
  background: #54b415 /* green */;
}

.diff-layout-changed:not(.djs-connection) .djs-visual > :nth-child(1) {
  fill: #4d79a3 /* light-blue */ !important;
}

.diff-layout-changed.djs-connection .djs-visual > :nth-child(1) {
  stroke: #185085 /* blue */ !important;
}

.marker.marker-layout-changed {
  background: #185085 /* blue */;
}

.diff-changed:not(.djs-connection) .djs-visual > :nth-child(1) {
  fill: #fbc16c /* light-yellow */ !important;
}

.diff-changed.djs-connection .djs-visual > :nth-child(1) {
  stroke: #cd8318 /* yellow */ !important;
}

.marker.marker-changed {
  background: #cd8318 /* yellow */;
}
.highlight:not(.djs-connection) .djs-visual > :nth-child(1) {
  fill: green !important; /* color elements as green */
}
</style>
